import type { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { formatNurseName } from "../../../utils/formatters";
import type { NurseInShift } from "../../../utils/models";
import { HiringShiftType, isDayShift, matchesRoleGroup, RoleGroup, SHIFTS_PER_WEEK, ShiftFilter } from "../types";
import type { SimulationEntry, UnderutilizedNurse } from "./useHiringData";

const SHIFTS_PER_NURSE = 6; // ~3 per week x 2 weeks

/** Count simulation entries for role/date/shift type (slots already filled in simulation). */
function getSimCount(simulationData: SimulationEntry[], role: string, date: DateTime, shiftType: HiringShiftType): number {
	return simulationData.filter((s) => s.role === role && s.date.hasSame(date, "day") && s.shiftType === shiftType).length;
}

/** Chooses day or night shift based on filter and gaps; alternates when both are needed. */
function pickShiftType(filter: ShiftFilter, gap: { dayGap: number; nightGap: number }, assigned: number): HiringShiftType | null {
	if (filter === ShiftFilter.All && gap.dayGap > 0 && gap.nightGap > 0) {
		return assigned % 2 === 1 ? HiringShiftType.Night : HiringShiftType.Day;
	}
	if (gap.dayGap > 0 && filter !== ShiftFilter.Night) return HiringShiftType.Day;
	if (gap.nightGap > 0 && filter !== ShiftFilter.Day) return HiringShiftType.Night;
	return null;
}

interface UseSimulationParams {
	dates: DateTime[];
	calculateNeeded: (role: string) => { day: number; night: number; total: number };
	getShiftsForDate: (date: DateTime) => NurseInShift[];
}

export const useSimulation = ({ dates, calculateNeeded, getShiftsForDate }: UseSimulationParams) => {
	const [simulationData, setSimulationData] = useState<SimulationEntry[]>([]);
	const lastNameFirst = useLastNameFirst();

	const resetSimulation = useCallback(() => setSimulationData([]), []);

	/** Adds underutilized nurses to simulation. Spaces shifts with at least 1 rest day between assignments. Caps per nurse and per week to avoid OT. */
	const addExistingToSimulation = useCallback(
		(role: string, filter: ShiftFilter, nurses: UnderutilizedNurse[]) => {
			setSimulationData((prev) => {
				const entries: SimulationEntry[] = [];

				for (const nurse of nurses) {
					const nurseName = formatNurseName(nurse.nurse, lastNameFirst);
					const alreadyInSim = prev.filter((s) => s.nurseName === nurseName && s.role === role);
					const alreadyPerWeek = [0, 0] as [number, number];
					for (const e of alreadyInSim) {
						const idx = dates.findIndex((d) => d.hasSame(e.date, "day"));
						if (idx >= 0) alreadyPerWeek[Math.floor(idx / 7)]++;
					}
					const capacityRemaining = Math.min(nurse.allowedShifts * 2 - nurse.scheduledShifts, SHIFTS_PER_NURSE - alreadyInSim.length);
					if (capacityRemaining <= 0) continue;

					const targetShift = filter === ShiftFilter.All ? nurse.shiftCategory : filter === ShiftFilter.Day ? HiringShiftType.Day : HiringShiftType.Night;

					const validDates = nurse.availableDates.filter((avail) => avail.shiftTypes.includes(targetShift));

					let lastIdx = -2;
					let added = 0;
					const addedPerWeek = [0, 0] as [number, number];

					for (let i = 0; i < validDates.length && added < capacityRemaining; i++) {
						if (i - lastIdx < 2) continue;
						const date = validDates[i].date;
						const idx = dates.findIndex((d) => d.hasSame(date, "day"));
						const weekIdx = idx >= 0 ? Math.floor(idx / 7) : 0;
						const countInWeek = (alreadyPerWeek[weekIdx] ?? 0) + (addedPerWeek[weekIdx] ?? 0);
						if (countInWeek >= SHIFTS_PER_WEEK) continue;

						entries.push({ date, role, shiftType: targetShift, nurseName, isNewHire: false });
						addedPerWeek[weekIdx]++;
						lastIdx = i;
						added++;
					}
				}

				return [...prev, ...entries];
			});
		},
		[dates, lastNameFirst],
	);

	/** Distributes new hires across dates, prioritizing most-needed days first and accounting for existing simulation. */
	const addHiresToSimulation = useCallback(
		(role: string, count: number, filter: ShiftFilter) => {
			const label = role === RoleGroup.Nurse ? "Nurse" : "Aide";

			setSimulationData((prev) => {
				const entries: SimulationEntry[] = [];

				// Gaps = needed - actual - already in simulation (use latest prev to avoid stale closure)
				const dateGaps = dates.map((date) => {
					const needed = calculateNeeded(role);
					const shifts = getShiftsForDate(date).filter((s) => matchesRoleGroup(s.role, role));
					const actualDay = shifts.filter((s) => isDayShift(s.shiftType)).length;
					const actualNight = shifts.filter((s) => !isDayShift(s.shiftType)).length;
					const simDay = getSimCount(prev, role, date, HiringShiftType.Day);
					const simNight = getSimCount(prev, role, date, HiringShiftType.Night);

					return {
						date,
						dayGap: filter !== ShiftFilter.Night ? Math.max(0, needed.day - actualDay - simDay) : 0,
						nightGap: filter !== ShiftFilter.Day ? Math.max(0, needed.night - actualNight - simNight) : 0,
					};
				});

				for (let i = 0; i < count; i++) {
					const name = `${label} ${i + 1}`;
					let assigned = 0;
					let lastDateIdx = -2;
					const assignedDatesForNurse = new Set<string>(); // one shift per calendar day per nurse (no same-day day+night)
					const assignedPerWeek = [0, 0] as [number, number]; // max 3 per week to avoid OT

					while (assigned < SHIFTS_PER_NURSE) {
						let bestIdx = -1;
						let bestShift: HiringShiftType | null = null;
						let bestGap = -1;

						for (let idx = 0; idx < dates.length; idx++) {
							const weekIdx = Math.floor(idx / 7);
							if (assignedPerWeek[weekIdx] >= SHIFTS_PER_WEEK) continue; // cap 3 per week

							const dateKey = dates[idx].toISODate() ?? "";
							if (assignedDatesForNurse.has(dateKey)) continue;
							const diff = Math.abs(idx - lastDateIdx);
							if (diff < 2 && diff !== dates.length - 1) continue;
							const gap = dateGaps[idx];
							const shift = pickShiftType(filter, gap, assigned);
							if (!shift) continue;
							const g = shift === HiringShiftType.Day ? gap.dayGap : gap.nightGap;
							if (g > bestGap) {
								bestGap = g;
								bestIdx = idx;
								bestShift = shift;
							}
						}

						if (bestIdx < 0 || bestShift === null) break;

						const gap = dateGaps[bestIdx];
						const dateKey = gap.date.toISODate() ?? "";
						const weekIdx = Math.floor(bestIdx / 7);
						assignedDatesForNurse.add(dateKey);
						assignedPerWeek[weekIdx]++;
						entries.push({ date: gap.date, role, shiftType: bestShift, nurseName: name, isNewHire: true });
						if (bestShift === HiringShiftType.Day) gap.dayGap--;
						else gap.nightGap--;
						assigned++;
						lastDateIdx = bestIdx;
					}
				}

				return [...prev, ...entries];
			});
		},
		[dates, calculateNeeded, getShiftsForDate],
	);

	/** Adds a custom nurse simulation for selected dates (from Simulate modal). Caps at SHIFTS_PER_NURSE and 3 per week to avoid OT. */
	const addCustomSimulation = useCallback(
		(role: string, shiftFilter: ShiftFilter, nurseName: string, selectedDates: DateTime[]) => {
			const name = nurseName.trim() || "Simulated";
			const shiftType = shiftFilter === ShiftFilter.Day ? HiringShiftType.Day : HiringShiftType.Night;

			setSimulationData((prev) => {
				const alreadyInSim = prev.filter((s) => s.nurseName === name && s.role === role);
				const alreadyPerWeek = [0, 0] as [number, number];
				for (const e of alreadyInSim) {
					const idx = dates.findIndex((d) => d.hasSame(e.date, "day"));
					if (idx >= 0) alreadyPerWeek[Math.floor(idx / 7)]++;
				}
				const slotsLeft = Math.max(0, SHIFTS_PER_NURSE - alreadyInSim.length);

				const addedPerWeek = [0, 0] as [number, number];
				const entries: SimulationEntry[] = [];

				for (const date of selectedDates) {
					if (entries.length >= slotsLeft) break;
					const idx = dates.findIndex((d) => d.hasSame(date, "day"));
					const weekIdx = idx >= 0 ? Math.floor(idx / 7) : 0;
					const countInWeek = (alreadyPerWeek[weekIdx] ?? 0) + (addedPerWeek[weekIdx] ?? 0);
					if (countInWeek >= SHIFTS_PER_WEEK) continue;

					entries.push({ date, role, shiftType, nurseName: name, isNewHire: false });
					addedPerWeek[weekIdx]++;
				}
				return [...prev, ...entries];
			});
		},
		[dates],
	);

	return { simulationData, resetSimulation, addExistingToSimulation, addHiresToSimulation, addCustomSimulation };
};
